/* eslint-disable no-await-in-loop */
// ./src/pages/privates/Contract/New/StepPayment/index.tsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { FormHandles } from '@unform/core';
import { FiPercent } from 'react-icons/fi';
import { IoIosTimer } from 'react-icons/io';
import {
  IoBarcodeOutline,
  IoCalendarOutline,
  IoCardOutline,
} from 'react-icons/io5';

import { SectionsItem } from '@components/forms/FormUserAddress/styles';
import {
  SubtitleSecondary,
  Paragraph,
  ButtonMain,
  ButtonDefault,
  CardSimpleButtonSelectable,
  InputSelect,
  AlertSimpleCustom,
  InputText,
} from '@components/index';
import { InputDate } from '@components/inputs/InputDate';
import {
  ConfigLabel,
  ConfigRules,
  ConfigStyles,
  ConfigTransition,
  ConfigValues,
} from '@config/index';
import Product from '@models/Product';
import { apiRebox, newContractStorageService } from '@services/index';
import { formatMoney } from '@utils/formatters';
import { toastify } from '@utils/notifiers';

import { schema } from './schemaValidation';
import { IFormStepPayment, ISelect } from './typing';

import { SectionsGroup } from '../StepCustomer/styles';
import {
  Container,
  SectionsPayment,
  FormPayment,
  DueDate,
  DueDateGroup,
  PaymentMethod,
  PaymentMethodOptions,
  PaymentMethodGroup,
  PaymentMethodGroupFields,
  Discount,
  DiscountButton,
  DiscountGroup,
  DiscountFields,
  DiscountItem,
  Buttons,
} from './styles';

interface IProps {
  myStep: number;
  currentStep: number;
  changeStep(newStep: number, willChangeHistory?: boolean): void;
}

const LIMIT_MAX_INSTALLMENTS = 12;

const StepPayment: React.FC<IProps> = ({ currentStep, changeStep }) => {
  const formRef = useRef<FormHandles>(null);

  const paymentSaved = useMemo(
    () => newContractStorageService.getPayment(),
    [],
  );

  const [loading, setLoading] = useState<boolean>(false);
  const [selectedDueDate, setSelectedDueDate] = useState<string>(
    paymentSaved.due_date || ConfigValues.rebox.contract.due_date.five,
  );
  const [selectedFormOfPayment, setSelectedFormOfPayment] = useState<string>(
    paymentSaved.form_of_payment ||
      ConfigValues.rebox.contract.form_of_payment.boleto,
  );
  const [product, setProduct] = useState<Product[]>([] as Product[]);
  const [installmentOptions, setInstallmentOptions] = useState<ISelect[]>([]);
  const [showDiscount, setShowDiscount] = useState<boolean>(
    !!(
      paymentSaved.discount_type &&
      paymentSaved.discount_type !==
        ConfigValues.rebox.contract.discount_type.undefined
    ),
  );
  const [
    enabledFieldsDiscountNumber,
    setEnabledFieldsDiscountNumber,
  ] = useState<boolean>(true);
  const [
    enabledFieldsDiscountAmount,
    setEnabledFieldsDiscountAmount,
  ] = useState<boolean>(true);

  const [selectedInstallDate, setSelectedInstallDate] = useState<Date | null>(
    null,
  );

  const [selectedPeriod, setSelectedPeriod] = useState<string>('');

  const initialData = useMemo(() => {
    const {
      discount_amount_installments,
      number_installments_with_discount,
    } = newContractStorageService.getPayment();
    return {
      discount_amount_installments: discount_amount_installments
        ? formatMoney.fromNumberToPrice(discount_amount_installments)
        : 'R$ 0,00',
      number_installments_with_discount: number_installments_with_discount || 0,
    };
  }, []);

  const getProduct = useCallback(() => {
    const cart = newContractStorageService.getCart();
    setProduct(cart);
  }, []);

  const changeDiscountType = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = event.target;
    const { discount_type } = ConfigValues.rebox.contract;

    setEnabledFieldsDiscountNumber(false);
    setEnabledFieldsDiscountAmount(false);
    const discountData = {
      discount_amount_installments: 'R$ 0,00',
      number_installments_with_discount: 0,
    };

    if (value === discount_type.undefined) {
      setEnabledFieldsDiscountNumber(true);
      setEnabledFieldsDiscountAmount(true);
    }

    if (value === discount_type.single) {
      discountData.number_installments_with_discount = 1;
      setEnabledFieldsDiscountNumber(true);
    }

    if (value === discount_type.recurrent) {
      discountData.number_installments_with_discount = LIMIT_MAX_INSTALLMENTS;
    }

    formRef.current?.setData(discountData);
  };

  const generateInstallmentOptions = useCallback(
    (prod: Product) => {
      const { promotional_price, current_price, coverage_months_limit } = prod;
      const amount = promotional_price || current_price || 0;

      const installments: ISelect[] = [];
      for (let index = 0; index < coverage_months_limit; index++) {
        const installmentCurrent = index + 1;
        let installmentValue = amount * coverage_months_limit;
        installmentValue /= installmentCurrent;
        installments.push({
          label: `${installmentCurrent}x de ${formatMoney.fromNumberToPrice(
            installmentValue,
          )} sem juros`,
          value: `${installmentCurrent}`,
        });
      }
      setInstallmentOptions(installments);
    },
    [product],
  );

  const handleSavePayment = useCallback(
    async (data: IFormStepPayment) => {
      try {
        setLoading(prevState => !prevState);
        formRef.current?.setErrors({});

        const isTrackerPlan = localStorage.getItem('is_tracker_plan');
        const savedData = localStorage.getItem('selected-date');
        const savedPeriod = localStorage.getItem('selected-hour');
        if (isTrackerPlan === 'true') {
          if (!savedData) {
            throw new Error(
              'Por favor, insira uma data de instalação antes de finalizar',
            );
          }

          if (!savedPeriod) {
            throw new Error(
              'Por favor, insira um periodo para instalação antes de finalizar',
            );
          }
        }

        await schema.validate(data, {
          abortEarly: false,
        });

        newContractStorageService.updatePayment({
          due_date: selectedDueDate,
          form_of_payment: selectedFormOfPayment,
          cycle: ConfigValues.rebox.contract.cycle.monthly,
          discount_type:
            data.discount_type ||
            ConfigValues.rebox.contract.discount_type.undefined,
          discount_amount_installments: data.discount_amount_installments
            ? formatMoney.revertToFloat(data.discount_amount_installments)
            : 0,
          number_installments_with_discount:
            data.number_installments_with_discount || 0,
          installments: Number.parseInt(data.installments, 10),
          due_date_if_plan_tracker: savedData || '',
          cycle_time_for_installation: savedPeriod || '',
        });

        changeStep(currentStep + 1);
      } catch (error: any) {
        toastify(
          typeof error.message === 'string'
            ? error.message
            : 'Oops! Houve um erro ao tentar salvar os dados de pagamento.',
          'error',
        );
      } finally {
        setLoading(prevState => !prevState);
      }
    },
    [selectedDueDate, selectedFormOfPayment],
  );

  const isTrackerPlan = () => {
    const savedBoolean = localStorage.getItem('is_tracker_plan');
    return savedBoolean ? JSON.parse(savedBoolean) : false;
  };

  const handleSelectedDate = (date: Date | null) => {
    setSelectedInstallDate(date);
  };

  useEffect(() => {
    getProduct();

    const datePickers = document.getElementsByClassName(
      'react-datepicker__input-container',
    );
    Array.from(datePickers).forEach(el =>
      el.children[0].setAttribute('readOnly', 'true'),
    );
  }, []);

  useEffect(() => {
    for (let index = 0; index < product.length; index++) {
      const prod = product[index];
      generateInstallmentOptions(prod);
    }
  }, [product]);

  useEffect(() => {
    if (selectedInstallDate) {
      const dueDateSelected = selectedInstallDate?.toISOString().split('T')[0];
      localStorage.setItem('selected-hour', selectedPeriod);
      localStorage.setItem('selected-date', dueDateSelected);
    }
  }, [selectedInstallDate, selectedPeriod]);

  return (
    <Container>
      <SubtitleSecondary textAlign="start" nameColor="black">
        Pagamento
      </SubtitleSecondary>
      <Paragraph
        textAlign="start"
        nameColor="black"
        opacity={0.8}
        fontWeight={500}
      >
        Escolha uma forma de pagamento e o vencimento da fatura
      </Paragraph>
      <SectionsPayment>
        <FormPayment
          ref={formRef}
          onSubmit={handleSavePayment}
          initialData={initialData}
        >
          <AlertSimpleCustom
            text={`Estamos considerando por padrão o ciclo de cobrança "mensal".`}
            type="info"
          />
          <DueDate>
            <SubtitleSecondary textAlign="start" fontSize={16}>
              Selecione o vencimento
            </SubtitleSecondary>
            <DueDateGroup>
              {ConfigValues.rebox.contract.due_date.array.map(item => (
                <CardSimpleButtonSelectable
                  key={item}
                  icon={{
                    element: IoCalendarOutline,
                    colorDefault:
                      ConfigStyles.rebox.colors.black.opacity.average,
                    colorActive: ConfigStyles.rebox.colors.white.main,
                    opacity: 1,
                    size: 20,
                  }}
                  label={{
                    text: item,
                    colorDefault:
                      ConfigStyles.rebox.colors.black.opacity.average,
                    colorActive: ConfigStyles.rebox.colors.white.main,
                  }}
                  positionContent="center"
                  isSelected={selectedDueDate === item}
                  onClick={() => setSelectedDueDate(item)}
                />
              ))}
            </DueDateGroup>
          </DueDate>
          <PaymentMethod>
            <SubtitleSecondary textAlign="start" fontSize={16}>
              Forma de pagamento
            </SubtitleSecondary>
            <PaymentMethodOptions>
              {[
                ConfigValues.rebox.contract.form_of_payment.credit_card,
                ConfigValues.rebox.contract.form_of_payment.boleto,
              ].map(item => (
                <CardSimpleButtonSelectable
                  key={item}
                  icon={{
                    element:
                      ConfigValues.rebox.contract.form_of_payment.boleto ===
                      item
                        ? IoBarcodeOutline
                        : IoCardOutline,
                    colorDefault:
                      ConfigStyles.rebox.colors.black.opacity.average,
                    colorActive: ConfigStyles.rebox.colors.white.main,
                    opacity: 1,
                    size: 20,
                  }}
                  label={{
                    text:
                      ConfigTransition.rebox_contracts_form_of_payment[
                        item.toLowerCase()
                      ],
                    colorDefault:
                      ConfigStyles.rebox.colors.black.opacity.average,
                    colorActive: ConfigStyles.rebox.colors.white.main,
                  }}
                  width={{ size: '240px', maxSize: '100%' }}
                  onClick={() => setSelectedFormOfPayment(item)}
                  isSelected={selectedFormOfPayment === item}
                  positionContent="flex-start"
                  style={{ marginRight: '2vh' }}
                />
              ))}
            </PaymentMethodOptions>
            {isTrackerPlan() ? (
              <>
                {selectedFormOfPayment ===
                  ConfigValues.rebox.contract.form_of_payment.boleto && (
                  <PaymentMethodGroup>
                    <Paragraph
                      nameColor="black"
                      textAlign="start"
                      fontWeight={500}
                    >
                      Escolha uma data para instalação
                    </Paragraph>
                    <Paragraph
                      nameColor="black"
                      textAlign="start"
                      opacity={0.8}
                      style={{ margin: '1.5vh 0 3vh' }}
                    >
                      Essa data de instalação é exclusivamente para instalação
                      do dispositivo rastreador.
                    </Paragraph>
                    <AlertSimpleCustom
                      text="As cobranças serão geradas automaticamente 1 mês após a data escolhida."
                      type="warning"
                    />
                    <InputDate
                      handleSelectedDate={handleSelectedDate}
                      name="due_date_if_plan_tracker"
                      value={selectedInstallDate}
                    />

                    {selectedInstallDate && (
                      <SectionsGroup>
                        <SectionsItem
                          style={{ display: 'flex', flexDirection: 'column' }}
                        >
                          <SubtitleSecondary
                            textAlign="start"
                            nameColor="black"
                            fontSize={14}
                          >
                            Selecione o horário de instalação
                          </SubtitleSecondary>

                          <div
                            style={{
                              display: 'flex',
                              gap: '0.5em',
                              width: '100%',
                            }}
                          >
                            {ConfigValues.rebox.product.time_installation.cycle_time.array.map(
                              cycle_time => (
                                <DueDate key={cycle_time}>
                                  <CardSimpleButtonSelectable
                                    style={{
                                      border: '1px solid rgba(0,0,0, .1)',
                                      width: '8em',
                                    }}
                                    key={0}
                                    label={{
                                      text: cycle_time,
                                      colorDefault:
                                        ConfigStyles.rebox.colors.black.opacity
                                          .average,
                                      colorActive:
                                        ConfigStyles.rebox.colors.white.main,
                                    }}
                                    icon={{
                                      element: IoIosTimer,
                                      size:
                                        ConfigStyles.rebox.fonts.size.paragraph
                                          .large,
                                      colorDefault:
                                        ConfigStyles.rebox.colors.black.main,
                                      colorActive:
                                        ConfigStyles.rebox.colors.black.main,
                                      opacity: 1,
                                    }}
                                    positionContent="center"
                                    isSelected={selectedPeriod === cycle_time}
                                    onClick={() =>
                                      setSelectedPeriod(cycle_time)
                                    }
                                  />
                                </DueDate>
                              ),
                            )}
                          </div>
                        </SectionsItem>
                      </SectionsGroup>
                    )}
                    <Paragraph
                      nameColor="black"
                      textAlign="start"
                      fontWeight={500}
                      style={{
                        marginTop: '2rem',
                      }}
                    >
                      Dados do boleto
                    </Paragraph>
                    <Paragraph
                      nameColor="black"
                      textAlign="start"
                      opacity={0.8}
                      style={{ margin: '1.5vh 0 3vh' }}
                    >
                      Esta venda será efetivada somente após o pagamento do 1º
                      boleto. A compensação de pagamento ocorre em até{' '}
                      {ConfigRules.rebox.charge.maximumDaysForCompensation} dias
                      úteis.
                    </Paragraph>

                    <AlertSimpleCustom
                      text="Independente do parcelamento, os boletos serão enviados ao cliente ciclo a ciclo desta venda."
                      type="warning"
                    />
                    <PaymentMethodGroupFields>
                      <InputSelect
                        name="installments"
                        options={installmentOptions}
                        placeholder="Selecione"
                        selectedDefault={`${LIMIT_MAX_INSTALLMENTS}`}
                        tabIndex={1}
                        // disabled={true}
                        // isDisable={true}
                      />
                    </PaymentMethodGroupFields>
                  </PaymentMethodGroup>
                )}
              </>
            ) : (
              <>
                {selectedFormOfPayment ===
                  ConfigValues.rebox.contract.form_of_payment.boleto && (
                  <PaymentMethodGroup>
                    <Paragraph
                      nameColor="black"
                      textAlign="start"
                      fontWeight={500}
                    >
                      Dados do boleto
                    </Paragraph>
                    <Paragraph
                      nameColor="black"
                      textAlign="start"
                      opacity={0.8}
                      style={{ margin: '1.5vh 0 3vh' }}
                    >
                      Esta venda será efetivada somente após o pagamento do 1º
                      boleto. A compensação de pagamento ocorre em até{' '}
                      {ConfigRules.rebox.charge.maximumDaysForCompensation} dias
                      úteis.
                    </Paragraph>
                    <AlertSimpleCustom
                      text="Independente do parcelamento, os boletos serão enviados ao cliente ciclo a ciclo desta venda."
                      type="warning"
                    />
                    <PaymentMethodGroupFields>
                      <InputSelect
                        name="installments"
                        options={installmentOptions}
                        placeholder="Selecione"
                        selectedDefault={`${LIMIT_MAX_INSTALLMENTS}`}
                        tabIndex={1}
                        // disabled={true}
                        // isDisable={true}
                      />
                    </PaymentMethodGroupFields>
                  </PaymentMethodGroup>
                )}
              </>
            )}

            {selectedFormOfPayment ===
              ConfigValues.rebox.contract.form_of_payment.credit_card && (
              <PaymentMethodGroup>
                <Paragraph nameColor="black" textAlign="start" fontWeight={500}>
                  Por enquanto indisponível
                </Paragraph>
              </PaymentMethodGroup>
            )}
          </PaymentMethod>

          <Discount>
            <DiscountButton
              type="button"
              onClick={() => setShowDiscount(prevState => !prevState)}
              tabIndex={2}
            >
              <FiPercent
                size={20}
                color={ConfigStyles.rebox.colors.blue.main}
              />
              <Paragraph nameColor="blue" textAlign="start" fontWeight={500}>
                Oferecer desconto?
              </Paragraph>
            </DiscountButton>
            {showDiscount && (
              <DiscountGroup>
                <Paragraph nameColor="black" textAlign="start" fontWeight={500}>
                  Dados de desconto
                </Paragraph>
                <Paragraph
                  nameColor="black"
                  textAlign="start"
                  opacity={0.8}
                  style={{ margin: '1.5vh 0' }}
                >
                  {`Apenas se definir um desconto, que invalida a aplicação automática
                do mesmo segundo critérios pré-definidos.`}
                </Paragraph>
                <DiscountFields>
                  <DiscountItem>
                    <SubtitleSecondary
                      textAlign="start"
                      nameColor="black"
                      fontSize={14}
                    >
                      Tipo de desconto
                    </SubtitleSecondary>
                    <Paragraph
                      nameColor="black"
                      textAlign="start"
                      opacity={0.5}
                      fontSize={13}
                      style={{ marginBottom: '2vh' }}
                    >
                      Define o ciclo de vida da aplicação do desconto
                    </Paragraph>
                    <InputSelect
                      name="discount_type"
                      options={ConfigLabel.rebox.others.contract.discount_type}
                      placeholder="Selecione"
                      selectedDefault={
                        paymentSaved.discount_type ||
                        ConfigValues.rebox.contract.discount_type.undefined
                      }
                      tabIndex={3}
                      onChange={changeDiscountType}
                    />
                  </DiscountItem>

                  <DiscountItem>
                    <SubtitleSecondary
                      textAlign="start"
                      nameColor="black"
                      fontSize={14}
                    >
                      Valor de desconto
                    </SubtitleSecondary>
                    <Paragraph
                      nameColor="black"
                      textAlign="start"
                      opacity={0.5}
                      fontSize={13}
                      style={{ marginBottom: '2vh' }}
                    >
                      Este valor será diminuído da cobrança do cliente
                    </Paragraph>

                    <InputText
                      name="discount_amount_installments"
                      placeholder="Apenas números"
                      tabIndex={4}
                      disabled={enabledFieldsDiscountAmount}
                      isDisable={enabledFieldsDiscountAmount}
                      onChange={value => {
                        value.target.value = formatMoney.replaceFloatPatternsForMoney(
                          value.target.value,
                        );
                      }}
                    />
                  </DiscountItem>
                </DiscountFields>

                <DiscountFields>
                  <DiscountItem>
                    <SubtitleSecondary
                      textAlign="start"
                      nameColor="black"
                      fontSize={14}
                    >
                      Número de parcelas
                    </SubtitleSecondary>
                    <Paragraph
                      nameColor="black"
                      textAlign="start"
                      opacity={0.5}
                      fontSize={13}
                      style={{ marginBottom: '2vh' }}
                    >
                      Limite máximo de parcelas a ser aplicado o desconto
                    </Paragraph>

                    <InputText
                      name="number_installments_with_discount"
                      placeholder="Limite máximo"
                      tabIndex={5}
                      disabled={enabledFieldsDiscountNumber}
                      isDisable={enabledFieldsDiscountNumber}
                    />
                  </DiscountItem>
                </DiscountFields>
              </DiscountGroup>
            )}
          </Discount>
        </FormPayment>
      </SectionsPayment>
      <Buttons>
        <ButtonMain
          loading={loading}
          onClick={() => formRef.current?.submitForm()}
          style={{ marginRight: '10px', maxWidth: 200 }}
        >
          Avançar
        </ButtonMain>
        <ButtonDefault
          onClick={() => changeStep(currentStep - 1)}
          style={{ maxWidth: 200 }}
          disabled={currentStep <= 1}
          isDisable={currentStep <= 1}
        >
          Voltar
        </ButtonDefault>
      </Buttons>
    </Container>
  );
};

export default StepPayment;
